import { z } from 'zod'

/**
 * แชท, staff talking to each other in Thananan OS (apps/api/src/modules/chat, apps/portal/src/chat): rooms of three
 * kinds, messages kept in our own database. Nobody reads a room they are not in, the assistant sees none of it
 * (docs/mcp.md), and a person who leaves keeps their messages, shown as gone.
 */

/** `direct`: two people · `group`: made by someone, who looks after it · `branch`: everyone at a branch, from HR */
export const chatRoomKinds = ['direct', 'group', 'branch'] as const
export type ChatRoomKind = (typeof chatRoomKinds)[number]

/** A group's admins rename it and add or remove people; anyone leaves a group, nobody leaves a branch's */
export const chatRoles = ['admin', 'member'] as const
export type ChatRole = (typeof chatRoles)[number]

/** How long after sending its writer may still change a message, or take it back */
export const chatEditMinutes = 15
export const chatUnsendHours = 24
export const chatTextMax = 4000
export const chatGroupNameMax = 80
/** One reaction each from these */
export const chatReactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const

export const chatMessageInput = z
  .object({
    /** Made on the device, so sending again after a lost connection sends it once */
    id: z.uuid(),
    text: z.string().trim().max(chatTextMax).default(''),
    replyToId: z.string().min(1).nullish(),
    /** Uploaded first (`POST /api/files`) */
    fileIds: z.array(z.string().min(1)).max(10).default([]),
    /** People of the room named with @ in the text */
    mentions: z.array(z.string().min(1)).max(50).default([]),
    /** Passed on from another room: shown as forwarded */
    forwarded: z.boolean().default(false),
  })
  .refine((m) => m.text.length > 0 || m.fileIds.length > 0, { message: 'พิมพ์ข้อความหรือแนบไฟล์' })
export type ChatMessageInput = z.input<typeof chatMessageInput>

export const chatRoomInput = z.discriminatedUnion('kind', [
  /** With one person: theirs and mine, made once */
  z.object({ kind: z.literal('direct'), userId: z.string().min(1) }),
  z.object({
    kind: z.literal('group'),
    name: z.string().trim().min(1).max(chatGroupNameMax),
    userIds: z.array(z.string().min(1)).min(1).max(200),
  }),
])
export type ChatRoomInput = z.input<typeof chatRoomInput>

/** Someone in a room, or who wrote in it */
export interface ChatPerson {
  userId: string
  name: string
  /** No longer at the company (their account is closed): their messages stay */
  gone?: boolean
}

export interface ChatMember extends ChatPerson {
  role: ChatRole
}

export interface ChatFile {
  id: string
  name: string
  type: string
  size: number
}

export interface ChatMessage {
  id: string
  roomId: string
  userId: string
  /** Empty once taken back */
  text: string
  mentions: string[]
  files: ChatFile[]
  /** What it answers, as it is now */
  replyTo: { id: string; userId: string; text: string; files: number; unsent: boolean } | null
  forwarded: boolean
  reactions: { emoji: string; userIds: string[] }[]
  /** Mine: how many others in the room have read it */
  readBy: number
  editedAt: string | null
  unsentAt: string | null
  createdAt: string
}

/** A room as the list shows it, from where I sit */
export interface ChatRoom {
  id: string
  kind: ChatRoomKind
  /** A group's name, a branch's, or the other person's in a direct room */
  name: string
  /** Direct: the other person */
  peer: ChatPerson | null
  branchId: string | null
  members: number
  last: { userId: string; text: string; files: number; unsent: boolean; at: string } | null
  /** Others' messages since I last read */
  unread: number
  /** Of those, how many name me */
  mentioned: number
  muted: boolean
  role: ChatRole
  /** When something was last said, or the room made */
  at: string
}

/** `GET /api/chat/rooms/:id` */
export interface ChatRoomDetail extends ChatRoom {
  people: ChatMember[]
  /** The announcement pinned at the top */
  pinned: ChatMessage | null
}

/** `GET /api/chat/rooms/:id/messages`: newest first; `before` the oldest one's id for more */
export interface ChatPage {
  items: ChatMessage[]
  more: boolean
}
